import {isExternalLink} from "../../utils/SchoolBlocksUtilities";

export function markdownToHtml(src: string, {currentFullUrl, allowGfmLinks}): string {
    // stolen from https://github.com/adamvleggett/drawdown/blob/master/drawdown.js and modified slightly
    let rx_space = /\t|\r|\uf8ff/g;
    let rx_escape = /\\([\\\|`*_{}\[\]()#&+\-~])/g;
    let rx_hr = /^([*\-=_] *){3,}$/gm;
    let rx_blockquote = /\n *> *([^]*?)(?=(\n|$){2})/g;
    let rx_list = /\n( *)(?:[*\-+]|((\d+)|([a-z])|[A-Z])[.)]) +([^]*?)(?=(\n|$){2})/g;
    let rx_listjoin = /<\/(ol|ul)>\n\n<\1>/g;
    let rx_code = /\n((```|~~~)\n?([^]*?)\n?\2|((    .*?\n)+))/g;
    let rx_link = /((!?)\[(.*?)\]\((.*?)( ".*")?\)|\\([\\`*_{}\[\]()#+\-.!~])|(<((?:\w{2,32}):(?:\S+))>))/g;
    let rx_table = /\n(( *\|.*?\| *\n)+)/g;
    let rx_thead = /^.*\n( *\|( *\:?-+\:?-+\:? *\|)* *\n|)/;
    let rx_row = /.*\n/g;
    let rx_cell = /\||(.*?[^\\])\|/g;
    let rx_heading = /(?=^|>|\n)([>\s]*?)(#{1,6}) (.*?)( #*)? *(?=\n|$)/g;
    let rx_para = /(?=^|>|\n)\s*\n+([^<]+?)\n+\s*(?=\n|<|$)/g;
    let rx_stash = /-\d+\uf8ff/g;
    let rx_email = /<([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})>/g;
    let rx_plain_email = /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g;

    function replace(rex, fn) {
        src = src.replace(rex, fn);
    }

    function element(tag, content, wrapInParagraph = false) {
        return '<' + tag + '>' + (wrapInParagraph ? '<p>' : '') + content + (wrapInParagraph ? '</p>' : '') + '</' + tag + '>';
    }

    function blockquote(src) {
        return src.replace(rx_blockquote, function(all, content) {
            return element('blockquote', highlight(content.replace(/\n *> */g, '\n')), true);
        });
    }

    function list(src) {
        return src.replace(rx_list, function(all, ind, ol, num, low, content) {
            const entry = element('li', content.split(
                RegExp(`\\n ?${ind}(?:(?:\\d+|[a-zA-Z])[.)]|[*\\-+]) +`, 'g'))
                .map(item => {
                    let processedItem = item;

                    processedItem = processedItem.replace(rx_link, function(all, p1, p2, p3, p4, p5, p6, p7, p8) {
                        if (p4) {
                            if (p2) {
                                stash[--si] = '<img src="' + p4 + '" alt="' + p3 + '"/>';
                            } else {
                                stash[--si] = `<a href="${unesc(p4)}" ${(!currentFullUrl || isExternalLink(p4, currentFullUrl)) ? "target='_blank' rel='nofollow noopener noreferrer'" : ""}>` + unesc(highlight(p3)) + '</a>';
                            }
                        } else if (p8) {
                            stash[--si] = `<a href="${unesc(p8)}" ${(!currentFullUrl || isExternalLink(p8, currentFullUrl)) ? "target='_blank' rel='nofollow noopener noreferrer'" : ""}>` + unesc(highlight(p8)) + '</a>';
                        } else {
                            stash[--si] = p6;
                        }
                        return si + '\uf8ff';
                    });

                    processedItem = processedItem.replace(rx_email, function(all, email) {
                        stash[--si] = `<a href="mailto:${email}">${email}</a>`;
                        return si + '\uf8ff';
                    });

                    processedItem = processedItem.replace(rx_plain_email, function(all, email) {
                        stash[--si] = `<a href="mailto:${email}">${email}</a>`;
                        return si + '\uf8ff';
                    });

                    return highlight(list(processedItem));
                })
                .join('</li><li>'));

            return '\n' + (ol
                ? `<ol start="${num ? ol : parseInt(ol,36) - 9}" ${!num ? `style="list-style-type:${low ? 'low' : 'upp'}er-alpha"` : ''}>${entry}</ol>`
                : element('ul', entry));
        });
    }

    function highlight(src) {
        // Handle bold-italic (***text*** or ___text___)
        src = src.replace(/(\*\*\*|___)((?:(?!\1)[^])*?)\1/g, function(all, marker, content) {
            return '<strong><em>' + highlight(content) + '</em></strong>';
        });

        // Handle bold (**text** or __text__)
        src = src.replace(/(\*\*|__)((?:(?!\1)[^])*?)\1/g, function(all, marker, content) {
            return '<strong>' + highlight(content) + '</strong>';
        });

        // Handle italic (*text* or _text_)
        src = src.replace(/(\*|_)((?:(?!\1)[^])*?)\1/g, function(all, marker, content) {
            return '<em>' + highlight(content) + '</em>';
        });

        // Handle strikethrough (~~text~~)
        src = src.replace(/(~~)((?:(?!\1)[^])*?)\1/g, function(all, marker, content) {
            return '<s>' + highlight(content) + '</s>';
        });

        // Handle superscript (^text^)
        src = src.replace(/(\^)((?:(?!\1)[^])*?)\1/g, function(all, marker, content) {
            return '<sup>' + highlight(content) + '</sup>';
        });

        // Handle small (--text--)
        src = src.replace(/(--)((?:(?!\1)[^])*?)\1/g, function(all, marker, content) {
            return '<small>' + highlight(content) + '</small>';
        });

        // Handle big (++text++)
        src = src.replace(/(\+\+)((?:(?!\1)[^])*?)\1/g, function(all, marker, content) {
            return '<big>' + highlight(content) + '</big>';
        });

        // Handle code spans with multiple backticks
        src = src.replace(/(`+)((?:(?!\1)[^])*?)\1/g, function(all, marker, content) {
            return '<code>' + content + '</code>';
        });

        return src;
    }

    function unesc(str) {
        return str.replace(rx_escape, '$1');
    }

    const stash: string[] = [];
    let si = 0;

    src = '\n' + src + '\n';

    replace(rx_space, '  ');

    // blockquote
    src = blockquote(src);

    // horizontal rule
    replace(rx_hr, '<hr/>');

    // list
    src = list(src);
    replace(rx_listjoin, '');

    // preserve code within code blocks
    replace(rx_code, function(all, p1, p2, p3, p4) {
        // @ts-ignore
        stash[--si] = element('pre', element('code', p3||p4.replace(/^    /gm, '')));
        return si + '\uf8ff';
    });

    // link or image
    replace(rx_link, function(all, p1, p2, p3, p4, p5, p6, p7, p8) {
        if (p4) {
            if (p2) {
                // @ts-ignore
                stash[--si] = '<img src="' + p4 + '" alt="' + p3 + '"/>';
            } else {
                // @ts-ignore
                stash[--si] =`<a href="${unesc(p4)}" ${(!currentFullUrl || isExternalLink(p4, currentFullUrl)) ? "target='_blank' rel='nofollow noopener noreferrer'" : ""}>` + unesc(highlight(p3)) + '</a>';
            }
        } else if (p8) {
            // @ts-ignore
            stash[--si] = `<a href="${unesc(p8)}" ${(!currentFullUrl || isExternalLink(p8, currentFullUrl)) ? "target='_blank' rel='nofollow noopener noreferrer'" : ""}>` + unesc(highlight(p8)) + '</a>';
        } else {
            // @ts-ignore
            stash[--si] = p6;
        }
        return si + '\uf8ff';
    });

    replace(rx_email, function(all, email) {
        // @ts-ignore
        stash[--si] = `<a href="mailto:${email}">${email}</a>`;
        return si + '\uf8ff';
    });

    replace(rx_plain_email, function(all, email) {
        // @ts-ignore
        stash[--si] = `<a href="mailto:${email}">${email}</a>`;
        return si + '\uf8ff';
    });

    // table
    replace(rx_table, function(all, table) {
        var sep = table.match(rx_thead)[1];
        return '\n' + element('table',
            table.replace(rx_row, function(row, ri) {
                return row == sep ? '' : element('tr', row.replace(rx_cell, function(all, cell, ci) {
                    return ci ? element(sep && !ri ? 'th' : 'td', unesc(highlight(cell || ''))) : ''
                }))
            })
        )
    });

    // heading
    replace(rx_heading, function(all, _, p1, p2) { return _ + element('h' + p1.length, unesc(highlight(p2))) });

    // paragraph
    replace(rx_para, function(all, content) { return element('p', unesc(highlight(content))) });

    // stash
    replace(rx_stash, function(all) { return stash[parseInt(all)] });

    return src.trim();
}

